import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Terminal, Wallet, ShieldCheck, CheckCheck, Activity, Bell, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import Reveal from "@/components/Reveal";
import { SectionLabel } from "@/components/common";
import { toast } from "sonner";

const snippets = {
  Python: `# Illustrative API — not a live endpoint
agent = vora.create_agent_wallet(name="Research Agent")

agent.set_spending_limit(monthly=500, transaction=100)

agent.require_approval(above=100)

payment = agent.authorize_payment(
    merchant="OpenAI API",
    amount=24.00,
)`,
  "Node.js": `// Illustrative API — not a live endpoint
const agent = await vora.createAgentWallet({ name: "Research Agent" });

await agent.setSpendingLimit({ monthly: 500, transaction: 100 });

await agent.requireApproval({ above: 100 });

const payment = await agent.authorizePayment({
  merchant: "OpenAI API",
  amount: 24.0,
});`,
  cURL: `# Illustrative API — not a live endpoint
curl https://api.vora.example/v1/agents \\
  -H "Authorization: Bearer $VORA_KEY" \\
  -d name="Research Agent" \\
  -d monthly_limit=500 \\
  -d transaction_limit=100 \\
  -d approval_above=100`,
};

const devFeatures = [
  { icon: Wallet, title: "Agent wallets", desc: "Provision an isolated wallet per agent." },
  { icon: CheckCheck, title: "Payment authorization", desc: "Authorize payments against live policy." },
  { icon: ShieldCheck, title: "Spending policies", desc: "Define limits, budgets and merchant rules." },
  { icon: CheckCheck, title: "Approval workflows", desc: "Route sensitive payments to a human." },
  { icon: Activity, title: "Transaction events", desc: "Stream every transaction as it happens." },
  { icon: Bell, title: "Monitoring", desc: "Track spend, risk and status in real time." },
];

export default function Developers() {
  const [tab, setTab] = useState("Python");
  const navigate = useNavigate();

  return (
    <div>
      <section className="relative overflow-hidden vora-hero-radial border-b border-white/8">
        <div className="absolute inset-0 vora-grid-bg opacity-40" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-24 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full border border-[#4F8CFF]/25 bg-[#4F8CFF]/8 px-3.5 py-1.5">
                <Terminal className="h-3.5 w-3.5 text-[#4F8CFF]" />
                <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#8fb4ff]">Developers</span>
              </span>
              <h1 className="mt-6 text-4xl sm:text-5xl font-bold tracking-tight leading-[1.08]">
                Give your AI agents a financial control layer.
              </h1>
              <p className="mt-6 max-w-xl text-lg text-slate-300">
                Vvvoraa is designed as infrastructure for developers building autonomous AI systems.
              </p>
              <div className="mt-8">
                <Button onClick={() => navigate("/early-access")} className="h-12 px-6 bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full font-semibold" data-testid="dev-waitlist-cta-top">
                  Join the Developer Waitlist <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="vora-glass rounded-2xl overflow-hidden shadow-[0_40px_80px_-30px_rgba(0,0,0,0.9)]">
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full bg-red-400/70" />
                    <span className="h-3 w-3 rounded-full bg-amber-400/70" />
                    <span className="h-3 w-3 rounded-full bg-emerald-400/70" />
                  </div>
                  <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-amber-400/80">Illustrative API</span>
                </div>
                <Tabs value={tab} onValueChange={setTab}>
                  <TabsList className="bg-transparent border-b border-white/8 rounded-none w-full justify-start h-auto p-0 px-2">
                    {Object.keys(snippets).map((k) => (
                      <TabsTrigger
                        key={k}
                        value={k}
                        className="data-[state=active]:bg-transparent data-[state=active]:text-[#4F8CFF] data-[state=active]:shadow-none text-slate-400 rounded-none border-b-2 border-transparent data-[state=active]:border-[#4F8CFF] px-4 py-2.5 text-xs font-mono"
                        data-testid={`code-tab-${k.toLowerCase().replace(/[^a-z]/g, "")}`}
                      >
                        {k}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  {Object.entries(snippets).map(([k, code]) => (
                    <TabsContent key={k} value={k} className="m-0">
                      <pre className="p-5 overflow-x-auto text-xs sm:text-[13px] leading-relaxed text-slate-200 font-mono">
                        <code>{code}</code>
                      </pre>
                    </TabsContent>
                  ))}
                </Tabs>
                <div className="px-4 py-3 border-t border-white/8 flex justify-end">
                  <Button
                    size="sm"
                    onClick={() => toast.success("Simulated run complete (demo)", { description: "authorizePayment() → status: pending_approval" })}
                    className="bg-[#4F8CFF]/15 text-[#4F8CFF] border border-[#4F8CFF]/25 hover:bg-[#4F8CFF]/25 rounded-full"
                    data-testid="run-code-btn"
                  >
                    Run Test Execution
                  </Button>
                </div>
              </div>
              <p className="mt-3 text-xs text-slate-500 text-center">
                Illustrative API. These methods are shown for design purposes and are not yet available.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal><SectionLabel>Developer Features</SectionLabel></Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {devFeatures.map((f, i) => {
              const Icon = f.icon;
              return (
                <Reveal key={f.title} delay={(i % 3) * 0.06}>
                  <div className="h-full vora-card vora-card-hover rounded-2xl p-6" data-testid={`dev-feature-${i + 1}`}>
                    <Icon className="h-5 w-5 text-[#4F8CFF]" />
                    <h3 className="mt-4 text-base font-semibold text-white">{f.title}</h3>
                    <p className="mt-2 text-sm text-slate-400">{f.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-12 text-center">
              <Button onClick={() => navigate("/early-access")} className="h-12 px-7 bg-white text-[#07111F] font-semibold rounded-full hover:bg-slate-100" data-testid="dev-waitlist-cta-bottom">
                Join the Developer Waitlist
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
